import { FC, useCallback, useState } from 'react';
import { InputSelect, MultiSelect, SectionToggle } from '@features/ui';
import {
  Control,
  Controller,
  ControllerRenderProps,
  UseFormGetValues,
  UseFormReset,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import {
  AddonTypeData,
  ProductFormData,
  ProductTypes,
  TOption,
} from '@src/types';
import {
  marketData,
  performanceAccountTypeData,
  productTypeData,
} from '@features/admin/productsAddons/productsAddonsData.ts';
import { TypeSelector } from '@features/ui/components';
import { ProductTradingAccount } from '@features/admin/productsAddons/ProductTradingAccount.tsx';
import { ChooseAccountType } from '@features/admin/productsAddons/ChooseAccountType.tsx';
import { ProductCompatibleDataFeeds } from '@features/admin/productsAddons/ProductCompatibleDataFeeds.tsx';
import { productTypeDefault } from '@features/admin/productsAddons/productDefault.ts';

interface ProductOptionsFormProps {
  productOptions: TOption[];
  isEdit?: boolean;
}

export const ProductTypeForm: FC<
  UseFormReturn<ProductFormData> & ProductOptionsFormProps
> = ({ productOptions, isEdit, ...methods }) => {
  const [hide, setHide] = useState(false);

  const {
    control,
    formState: { errors },
    reset,
    getValues,
  } = methods;

  const type = useWatch({ control, name: 'category' });

  const renderAllProductTypeContent = useCallback(() => {
    return (
      <>
        {/* Trading Account */}
        <div
          style={{
            display: type === ProductTypes.TRADING_ACCOUNT ? 'block' : 'none',
          }}
        >
          <ProductTradingAccount {...methods} />
        </div>

        {/* Add-ons Edge Enhancers */}
        <div
          className={'lg:w-[50%] w-full'}
          style={{
            display:
              type === ProductTypes.ADD_ONS_EDGE_ENHANCERS ? 'block' : 'none',
          }}
        >
          <ChooseAccountType
            control={control as unknown as Control<AddonTypeData>}
            getValues={getValues as unknown as UseFormGetValues<AddonTypeData>}
            productOptions={productOptions}
            reset={reset as unknown as UseFormReset<AddonTypeData>}
          />
          <ProductCompatibleDataFeeds
            className={'mt-7'}
            control={control}
            errors={errors}
          />
        </div>

        {/* Resets */}
        <div
          className={'lg:w-[50%] w-full mt-6'}
          style={{ display: type === ProductTypes.RESETS ? 'block' : 'none' }}
        >
          <Controller
            control={control}
            name="allow_reset_of_packages"
            render={({ field }) => (
              <div className={'flex flex-col gap-1'}>
                <p className={'ml-2'}>Allow reset of packages</p>
                <MultiSelect
                  options={productOptions}
                  placeholder={'Select'}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
          <ProductCompatibleDataFeeds
            className={'mt-7'}
            control={control}
            errors={errors}
          />
        </div>

        {/* Market Data */}
        <div
          className={'lg:w-[50%] w-full mt-6'}
          style={{
            display: type === ProductTypes.MARKET_DATA ? 'block' : 'none',
          }}
        >
          <Controller
            control={control}
            name="market_data_type"
            render={({ field }) => (
              <div className={'flex flex-1 flex-col gap-2'}>
                <p className={'ml-2'}>Market Data</p>
                <InputSelect
                  options={marketData}
                  placeholder={'Select'}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
          <ProductCompatibleDataFeeds
            className={'mt-7'}
            control={control}
            errors={errors}
          />
        </div>

        {/* Other */}
        <div
          className={'lg:w-[50%] w-full mt-6'}
          style={{
            display: type === ProductTypes.OTHER ? 'block' : 'none',
          }}
        >
          <Controller
            control={control}
            name="performance_account_type"
            render={({ field }) => (
              <div className={'flex flex-1 flex-col gap-2'}>
                <p className={'ml-2'}>Performance Account Type</p>
                <InputSelect
                  options={performanceAccountTypeData}
                  placeholder={'Select'}
                  value={field.value ?? ''}
                  onChange={(value) => {
                    const currentValue = getValues();

                    reset({
                      ...currentValue,
                      ...productTypeDefault,
                    });
                    field.onChange(value);
                  }}
                />
              </div>
            )}
          />
        </div>
      </>
    );
  }, [type, methods, control, getValues, productOptions, reset, errors]);

  const handleChangeCategory = useCallback(
    (
      field: ControllerRenderProps<ProductFormData, 'category'>,
      val: ProductTypes
    ) => {
      const currentValue = getValues();

      reset({
        ...currentValue,
        ...productTypeDefault,
        category: val,
      });
      field.onChange(val);
    },
    [getValues, reset]
  );

  return (
    <div
      className={
        'text-xs mb-[47px] border-dotted border-[#FFFFFF] border-t-[1.5px]'
      }
    >
      <SectionToggle
        className={'mt-10'}
        label={'Product Type'}
        toggle={hide}
        onChange={setHide}
      />
      <div
        className={'mt-[31px] lg:ml-10 ml-5'}
        style={{
          display: !hide ? 'block' : 'none',
        }}
      >
        <Controller
          control={control}
          name="category"
          render={({ field }) => {
            const indexOfType = productTypeData.findIndex(
              (option) => option.value === field.value
            );

            return (
              <div className={'w-fit'}>
                <TypeSelector
                  disabled={isEdit}
                  tabActive={indexOfType >= 0 ? indexOfType : 0}
                  tabs={productTypeData}
                  onChangeTab={(_, item) =>
                    handleChangeCategory(field, item.value as ProductTypes)
                  }
                />
                <div
                  className={'flex-1 bg-white origin-top'}
                  style={{
                    height: '1px',
                    transform: 'scaleY(0.3)',
                  }}
                />
              </div>
            );
          }}
        />
        {renderAllProductTypeContent()}
      </div>
    </div>
  );
};
